/**
 * @fileoverview Custom hook for 2FA authentication flow
 */

import { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotify } from '../../../context/NotificationContext';
import { addRecentUser } from '../utils/recentUsers';

/**
 * Custom hook for managing 2FA authentication flow
 * @param {Object} options - Hook options
 * @param {Function} options.onSuccess - Callback on successful 2FA
 * @returns {Object} 2FA state and handlers
 */
export const use2FAFlow = ({ onSuccess }) => {
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [username2FA, setUsername2FA] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [hasBiometric, setHasBiometric] = useState(false);
  const [hasEmail, setHasEmail] = useState(false);
  const [emailCodeRequested, setEmailCodeRequested] = useState(false);
  const [needsPasskeySetup, setNeedsPasskeySetup] = useState(false);
  const [passkeyPromptDismissed, setPasskeyPromptDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  const { error } = useNotify();
  const {
    submit2FA,
    authenticatePasskeyWithToken,
    requestEmailCodeWithToken,
    APP_URL
  } = useAuth();

  /**
   * Initialize 2FA flow with the given parameters
   * @param {Object} params - 2FA parameters from login response
   */
  const initiate2FA = (params) => {
    setUsername2FA(params.username);
    setTempToken(params.tempToken);
    setHasBiometric(params.hasBiometric);
    setHasEmail(params.hasEmail);
    setNeedsPasskeySetup(params.needsPasskeySetup);
    setPasskeyPromptDismissed(params.passkeyPromptDismissed);
    setEmailCodeRequested(params.preferEmail || false);
    setShow2FAModal(true);
  };

  /**
   * Reset 2FA state
   */
  const reset2FA = () => {
    setShow2FAModal(false);
    setEmailCodeRequested(false);
    setNeedsPasskeySetup(false);
    setTempToken('');
    setUsername2FA('');
    setHasBiometric(false);
    setHasEmail(false);
  };

  /**
   * Handle 2FA code submission
   * @param {Event} e - Form submit event
   */
  const handle2FASubmit = async (e) => {
    e.preventDefault();
    const code = e.target.elements.code.value;

    if (!code || code.length !== 8) {
      error('Please enter a valid 8-digit authentication code');
      return;
    }

    setLoading(true);

    try {
      const result = await submit2FA(code);

      if (result.success) {
        // Save to recent users
        addRecentUser({
          username: username2FA,
          firstName: '',
          lastName: '',
          hasPasskey: hasBiometric
        });

        setShow2FAModal(false);
        setLoading(false);

        onSuccess({
          username: username2FA,
          hasPasskey: hasBiometric,
          needsPasskeySetup,
          passkeyPromptDismissed
        });
      } else {
        error(result.error || 'Invalid verification code');
        setLoading(false);
      }
    } catch (err) {
      error('An unexpected error occurred during verification');
      setLoading(false);
    }
  };

  /**
   * Request email code for 2FA
   */
  const handleRequestEmailCode = async () => {
    if (!username2FA || username2FA.trim().length === 0) {
      error('Session expired, please login again');
      setShow2FAModal(false);
      return;
    }

    setLoading(true);

    try {
      if (tempToken) {
        const result = await requestEmailCodeWithToken(tempToken);
        if (result.success) {
          setEmailCodeRequested(true);
        } else {
          error(result.error || 'Failed to send email code');
        }
      } else {
        // Legacy flow for backwards compatibility
        const response = await fetch(`${APP_URL}/login?action=request_2fa_code&username=${encodeURIComponent(username2FA)}`, {
          method: 'GET',
          credentials: 'include'
        });

        const data = await response.json();

        if (response.ok) {
          setEmailCodeRequested(true);
        } else {
          error(data.error || 'Failed to send email code');
        }
      }
    } catch (err) {
      error('An unexpected error occurred while requesting email code');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle biometric/passkey 2FA
   */
  const handleBiometric2FA = async () => {
    if (!username2FA || username2FA.trim().length === 0) {
      error('Session expired, please login again');
      setShow2FAModal(false);
      return;
    }

    setLoading(true);

    try {
      const result = await authenticatePasskeyWithToken(tempToken);

      if (result.success) {
        // Save to recent users
        addRecentUser({
          username: username2FA,
          firstName: '',
          lastName: '',
          hasPasskey: true
        });

        setShow2FAModal(false);
        setLoading(false);

        onSuccess({
          username: username2FA,
          hasPasskey: true,
          needsPasskeySetup,
          passkeyPromptDismissed
        });
      } else {
        error(result.error || 'Biometric authentication failed');
        setLoading(false);
      }
    } catch (err) {
      error('An unexpected error occurred during biometric authentication');
      setLoading(false);
    }
  };

  /**
   * Go back to 2FA choice screen
   */
  const goBack2FAChoice = () => {
    setEmailCodeRequested(false);
  };

  return {
    show2FAModal,
    username2FA,
    tempToken,
    hasBiometric,
    hasEmail,
    emailCodeRequested,
    needsPasskeySetup,
    passkeyPromptDismissed,
    loading,
    initiate2FA,
    reset2FA,
    handle2FASubmit,
    handleRequestEmailCode,
    handleBiometric2FA,
    goBack2FAChoice,
    setLoading
  };
};

export default use2FAFlow;
